import { createFileRoute } from '@tanstack/react-router';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import { AuctionIcon, InformationCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { MainContainer, MainHeaderBackButton, MainScrollArea } from '@/app/ui/components/main';
import { H1 } from '@/app/ui/components/heading';
import { Spinner } from '@/app/ui/components/spinner';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { Card } from '@/app/ui/components/card';
import { useGetTenderFeedbackOverview } from '@/app/hooks/queries/useTenderFeedbacks';
import TenderFeedbackPositiveNegativeList from '@/app/components/features/tenderFeedbacks/NewTenderFeedbackPositiveNegativeList';
import { Alert, AlertDescription, AlertTitle } from '@/app/ui/components/alert';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/tender-feedback',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tFeedback = useTranslations('components.tenderFeedbackDetails');
  const { bidId, tenderPackageId, projectId } = Route.useParams();
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const tenderFeedbackId = bid?.tenderFeedbackId;

  const { data, isLoading } = useGetTenderFeedbackOverview({
    payload: {
      feedbackId: tenderFeedbackId!,
      projectId,
      tenderId: tenderPackageId,
      bidId,
    },
    enabled: !!tenderFeedbackId,
  });

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeedback('header')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea>
        {isLoading ? (
          <Spinner kind="inset" />
        ) : (
          <MainContainer>
            <H1 className="mb-6">
              <MainHeaderBackButton />
              {tFeedback('header')}
            </H1>
            <Alert variant="info" className="mb-5">
              <HugeiconsIcon icon={InformationCircleIcon} />
              <AlertTitle>{tFeedback('alert.title')}</AlertTitle>
              <AlertDescription>{tFeedback('alert.subtitle')}</AlertDescription>
            </Alert>
            <Card>
              <div className="pb-10">
                <TenderFeedbackPositiveNegativeList
                  title={tFeedback('positives')}
                  description={tFeedback('assessmentDesc')}
                  items={data?.positives || []}
                  count={data?.positivesCount ?? 0}
                  isLoading={isLoading}
                  variant="positives"
                />
              </div>
              <TenderFeedbackPositiveNegativeList
                title={tFeedback('negatives')}
                description={tFeedback('assessmentDesc')}
                items={data?.negatives || []}
                count={data?.negativesCount ?? 0}
                isLoading={isLoading}
                variant="negatives"
              />
            </Card>
          </MainContainer>
        )}
      </MainScrollArea>
    </>
  );
}
